import "./common.styles.scss";
import ClickOutside from "./ClickOutsideComponent";
import LinkEmailToContact from "../popUpComponents/LinkEmailToContact";
import { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

// --- DND-KIT IMPORTS ---
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Task 24737 - a chip is "unlinked" when it carries no customer. That is
// exactly the state a hand-typed address lands in (see handleKeyPressed
// below, which builds firstLine.label as ""), and it means the email goes out
// as a raw string attributed to nobody.
const isChipUnlinked = (chip) =>
  !chip?.item?.CustomerToContactPersonRelationship_Id &&
  !String(chip?.firstLine?.label || "").trim();

// --- NEW SUB-COMPONENT FOR DRAGGABLE CHIPS ---
const SortableChip = ({ chip, onRemove, onLink }) => {
  // We use chip.id or fallback to chip.lid to ensure dnd-kit always has a unique identifier
  const sortableId = chip.id || chip.lid;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1, // Make it slightly transparent while dragging
    zIndex: isDragging ? 100 : "auto",
    cursor: "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={"custom-chip-input-chip"}
      {...attributes}
      {...listeners}
    >
      <div className={"custom-chip-input-chip-label"}>
        <div className={"custom-select-first-line"}>
          <i className={`${chip.firstLine?.icon} mx-1`}></i>
          {/* Task 24737 - the customer line is blank on a hand-typed address.
              It used to just sit there empty, which said nothing; now it says
              what is wrong and is the button that fixes it. */}
          {isChipUnlinked(chip) && onLink ? (
            <span
              role="button"
              tabIndex={0}
              className="custom-chip-input-chip-link-warning"
              title="Not linked to a customer - click to link this address to a contact"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onLink();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onLink();
                }
              }}
            >
              <i className="ri-error-warning-line me-1"></i>
              Link to a customer
            </span>
          ) : (
            chip.firstLine?.label
          )}
        </div>
        <div className={"custom-select-first-second-line"}>
          <i className={`${chip.secondLine?.icon} mx-1`}></i>
          {chip.secondLine?.label}
        </div>
        <div className={"custom-select-first-third-line"}>
          <i className={`${chip.thirdLine?.icon} mx-1`}></i>
          {chip.thirdLine?.label}
        </div>
      </div>
      <div
        className={"custom-chip-input-chip-close"}
        onMouseDown={(e) => {
          // CRITICAL: Prevent dnd-kit from starting a drag on desktop
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          // CRITICAL: Prevent dnd-kit from starting a drag on mobile/touch
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        title={"Remove"}
      >
        X
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---
export const CustomChipInput = ({
  id, // <-- NEW: Passed from SendEmail.js to identify the droppable zone ("to", "cc", "bcc")
  placeholder,
  value,
  onClickCallBack,
  options,
  style,
  addNewItem,
  addNewItemCallBack,
  chipList,
  setChipList,
}) => {
  // Grab 'isOver' to give the user visual feedback when hovering over an empty field
  const { setNodeRef, isOver } = useDroppable({ id: id || placeholder });

  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [allowedSearchFilters, setAllowedSearchFilters] = useState([
    "Title",
    "Product_Name",
    "Customer_FullName",
    "Id",
    "AddressLine1",
    "AddressLine2",
    "AddressLine3",
    "AddressLine4",
    "AddressCity",
    "AddressCounty",
    "AddressPostCode",
    "AddressCountry",
    "RegistrationPlate",
    "Chassis",
    "MinistryId",
    "Model",
    "ModelYear",
    "MileageValue",
    "SiteReference",
    "FullName",
    "ContactPerson_FullName",
    "ContactPersonEmailAddress_EmailAddress",
  ]);

  useEffect(() => {
    if (searchQuery) {
      const results = options?.filter((option) => {
        return allowedSearchFilters.some((filter) => {
          return option?.item[filter]
            ?.toLowerCase()
            ?.includes(searchQuery?.toLowerCase());
        });
      });
      setSearchResults(results);
    } else {
      setSearchResults(options);
    }
  }, [searchQuery, options]);

  // Task 24737 - which chip (by index) is being linked to a contact.
  const [linkChipIndex, setLinkChipIndex] = useState(null);
  const linkChip = linkChipIndex == null ? null : chipList[linkChipIndex];

  // Rebuild the chip from what the backend wrote, in exactly the shape the
  // address-book picker produces. This is the part that matters: after linking,
  // the chip carries item.CustomerToContactPersonRelationship_Id, so SendEmail
  // sends it as a contact rather than as a raw string, and the email lands
  // against the customer.
  //
  // The endpoint can touch several relationships at once (a new contact linked
  // to three customers), but the chip is one address in one box, so the first
  // row wins and the rest are simply saved to the contact for next time.
  const handleLinked = (rows) => {
    const contact = rows?.[0];
    if (!contact || linkChipIndex == null) return;

    setChipList(
      chipList.map((chip, i) =>
        i !== linkChipIndex
          ? chip
          : {
              ...chip,
              firstLine: {
                icon: "fa-solid fa-person-shelter",
                label: contact.Customer_FullName,
              },
              secondLine: {
                icon: "fa-solid fa-person",
                label: contact.ContactPerson_FullName,
              },
              thirdLine: {
                icon: "fa-solid fa-envelope",
                label: contact.ContactPersonEmailAddress_EmailAddress,
              },
              id: contact.CustomerToContactPersonRelationship_Id,
              value: contact.CustomerToContactPersonRelationship_Id,
              item: contact,
            }
      )
    );
    setLinkChipIndex(null);
  };

  const [selectedOption, setSelectedOption] = useState(null);
  const [tempPlaceHolder, setTempPlaceHolder] = useState(placeholder);
  useEffect(() => {
    setTempPlaceHolder(placeholder);
  }, [placeholder]);

  useEffect(() => {
    if (value) {
      const option = options?.find((option) => option.id === value);
      setSelectedOption(option);
    }
  }, [value, options]);

  const handleOptionSelection = (option) => {
    setSelectedOption(option);
    setShowSearchResults(false);
    setSearchQuery("");
    onClickCallBack(option);
  };

  const handleKeyPressed = (e) => {
    if (e?.key === "Enter" || e?.key === ";") {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
      const firstLastName = searchQuery
        ?.split("@")?.[0]
        ?.split(".")
        ?.map((item) => {
          return item.charAt(0).toUpperCase() + item.slice(1);
        })
        ?.join(" ");

      const lid = chipList?.length
        ? Math.max(...chipList.map((item) => item.id || item.lid || 0)) + 1
        : 1;

      if (emailRegex.test(searchQuery)) {
        setChipList([
          ...chipList,
          {
            id: `custom-${lid}`, // <-- Added id property so DND kit can track custom typed emails
            firstLine: { icon: "fa-solid fa-person-shelter", label: "" },
            secondLine: {
              icon: "fa-solid fa-person",
              label: firstLastName ?? "",
            },
            thirdLine: { icon: "fa fa-envelope", label: searchQuery },
            lid,
          },
        ]);
        setSearchQuery("");
      } else {
        toast.error("Invalid Email");
      }
    }
  };

  // Extract the IDs for the SortableContext
  const sortableItemIds = chipList.map((chip) => chip.id || chip.lid);

  return (
    <div>
      {/* Task 24737 - link a hand-typed address to a contact without leaving
          the half-written email. */}
      {linkChip && (
        <LinkEmailToContact
          open={!!linkChip}
          onClose={() => setLinkChipIndex(null)}
          emailAddress={linkChip?.thirdLine?.label}
          suggestedName={linkChip?.secondLine?.label}
          contactOptions={options}
          onLinked={handleLinked}
        />
      )}

      <ClickOutside
        onClick={() => {
          setShowSearchResults(false);
        }}
      >
        <div>
          {/* --- MOVED setNodeRef HERE: Now the whole input field is a drop zone --- */}
          <div
            ref={setNodeRef}
            className={"custom-chip-main-frame-wrapper w-100"}
            style={{
              minHeight: "45px", // Ensures there is physical space to drop into
              outline: isOver ? "2px dashed #00a459" : "none", // Adds a green dashed box when hovering
              outlineOffset: "-2px",
              transition: "outline 0.2s ease",
              padding: "4px", // Added a tiny bit of padding so the outline doesn't clip the chips
            }}
          >
            <div className={"d-flex flex-row w-100 flex-wrap"}>
              <SortableContext
                items={sortableItemIds}
                strategy={horizontalListSortingStrategy}
              >
                {!isEmpty(chipList) &&
                  chipList.map((chip, index) => (
                    <SortableChip
                      key={chip.id || chip.lid || index}
                      chip={chip}
                      onRemove={() => {
                        setChipList(chipList.filter((_, i) => i !== index));
                      }}
                      onLink={() => setLinkChipIndex(index)}
                    />
                  ))}
              </SortableContext>
            </div>

            <div className={"w-100"}>
              <div className={"d-flex"}>
                <input
                  placeholder={tempPlaceHolder}
                  className={" custom-chip-input"}
                  type={"text"}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  onFocus={() => {
                    setShowSearchResults(true);
                  }}
                  onKeyDown={(e) => {
                    handleKeyPressed(e);
                  }}
                />
                {chipList.length !== 0 && (
                  <div
                    className={
                      "d-flex align-items-center justify-content-center"
                    }
                  >
                    <button
                      className={"btn btn-sm btn-danger ms-2 d-flex "}
                      title={"Clear All"}
                      onClick={() => {
                        setChipList([]);
                      }}
                    >
                      <i className="fa-regular fa-trash-can"></i>
                    </button>
                  </div>
                )}
              </div>

              <div>
                {showSearchResults && (
                  <div>
                    <div
                      className={"custom-select-display-results-main-frame"}
                      style={style}
                    >
                      <div
                        className={
                          "custom-select-display-results-main-frame-wrapper"
                        }
                      >
                        {searchResults?.map((option, index) => (
                          <div
                            key={index}
                            className={
                              (index % 2 === 0
                                ? "custom-select-search-results-odd"
                                : "custom-select-search-results-even") +
                              " custom-select-search-results"
                            }
                            onClick={() => {
                              handleOptionSelection(option);
                            }}
                          >
                            <div>
                              <div className={"custom-select-first-line"}>
                                <i
                                  className={`${option.firstLine.icon} mx-1`}
                                ></i>
                                {option.firstLine.label}
                              </div>
                              <div
                                className={"custom-select-first-second-line"}
                              >
                                <i
                                  className={`${option.secondLine.icon} mx-1`}
                                ></i>
                                {option.secondLine.label}
                              </div>
                              <div className={"custom-select-first-third-line"}>
                                <i
                                  className={`${option.thirdLine.icon} mx-1`}
                                ></i>
                                {option.thirdLine.label}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ClickOutside>
    </div>
  );
};
